import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { ArrowLeft, Calculator, Send, Download, Loader2 } from "lucide-react";

// ROI Estimator — rep tool in the Resource Center.
// Mirrors the math from the ProspectMax deck slide 14 (and the public ROI calculator).
// The rep enters the prospect's numbers, sees a live projection, and can either email
// a co-branded PDF or download a ProspectMax deck with these numbers baked into slide 15.
const fmtMoney = (n) =>
  n == null || Number.isNaN(Number(n)) ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const fmtInt = (n) => (n == null || Number.isNaN(Number(n)) ? "—" : `${Math.round(Number(n)).toLocaleString()}`);

export default function ROIEstimatorPage() {
  const [inputs, setInputs] = useState({
    missed_calls_daily: "",
    missed_website_leads_daily: "",
    conversion_rate_pct: "",
    lifetime_value: "",
    monthly_program_cost: "",
  });
  const [prospect, setProspect] = useState({ name: "", company: "", email: "", note: "" });
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const numericInputs = useMemo(() => ({
    missed_calls_daily: parseFloat(inputs.missed_calls_daily) || 0,
    missed_website_leads_daily: parseFloat(inputs.missed_website_leads_daily) || 0,
    conversion_rate_pct: parseFloat(inputs.conversion_rate_pct) || 0,
    lifetime_value: parseFloat(inputs.lifetime_value) || 0,
    monthly_program_cost: parseFloat(inputs.monthly_program_cost) || 0,
  }), [inputs]);

  // Live ROI math — mirrors backend _compute_prospectmax_roi exactly so what reps
  // see on screen is what lands in the PDF + slide.
  const outputs = useMemo(() => {
    const missedMonthly = (numericInputs.missed_calls_daily + numericInputs.missed_website_leads_daily) * 30;
    const rate = Math.max(0, Math.min(numericInputs.conversion_rate_pct, 100)) / 100;
    const newCustomers = missedMonthly * rate;
    const revenue = newCustomers * numericInputs.lifetime_value;
    const grossProfit = revenue - numericInputs.monthly_program_cost;
    const roiPct = numericInputs.monthly_program_cost > 0
      ? (grossProfit / numericInputs.monthly_program_cost) * 100
      : 0;
    return {
      total_missed_opportunities_monthly: missedMonthly,
      new_customers_monthly: newCustomers,
      new_customer_revenue: revenue,
      gross_profit: grossProfit,
      roi_pct: roiPct,
    };
  }, [numericInputs]);

  const allInputsFilled = Object.values(inputs).every((v) => v !== "" && !Number.isNaN(parseFloat(v)));

  const handleEmail = async () => {
    if (!allInputsFilled) { toast.error("Fill in all five numbers first"); return; }
    if (!prospect.email) { toast.error("Enter the prospect's email"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API_BASE}/api/roi/rep-email`, {
        to_email: prospect.email,
        prospect_name: prospect.name || null,
        prospect_company: prospect.company || null,
        rep_note: prospect.note || null,
        inputs: numericInputs,
      });
      if (r.data?.email_sent === false) {
        // Server accepted the request but did not actually deliver — likely
        // TEST_MODE is on. Surface the reason so the rep doesn't assume the
        // prospect got the email.
        const status = r.data?.email_status;
        const msg = status === "test_mode"
          ? "TEST_MODE is enabled on this environment — the email was NOT sent. Ask your Super Admin to unset TEST_MODE on the deployment."
          : status === "no_resend_key"
          ? "Resend API key is not configured — the email was NOT sent."
          : "The server accepted the request but did not deliver the email.";
        toast.warning(msg, { duration: 12000 });
      } else {
        toast.success(`ROI projection emailed to ${prospect.email}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the ROI email");
    } finally {
      setSending(false);
    }
  };

  const handleDownloadDeck = async () => {
    if (!allInputsFilled) { toast.error("Fill in all five numbers first"); return; }
    setDownloading(true);
    try {
      const params = new URLSearchParams({
        missed_calls_daily: String(numericInputs.missed_calls_daily),
        missed_website_leads_daily: String(numericInputs.missed_website_leads_daily),
        conversion_rate_pct: String(numericInputs.conversion_rate_pct),
        lifetime_value: String(numericInputs.lifetime_value),
        monthly_program_cost: String(numericInputs.monthly_program_cost),
      });
      if (prospect.company) params.set("prospect_company", prospect.company);
      const res = await axios.get(`${API_BASE}/api/decks/prospectmax?${params.toString()}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      }));
      const safe = (prospect.company || "ProspectMax").replace(/[^A-Za-z0-9_-]+/g, "_");
      const a = document.createElement("a");
      a.href = url;
      a.download = `${safe}_ProspectMax_with_ROI.pptx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success("Deck downloaded with the prospect's numbers on slide 15");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not generate the deck");
    } finally {
      setDownloading(false);
    }
  };

  const presetClick = (preset) => setInputs(preset);

  return (
    <div className="space-y-5 max-w-6xl">
      <div>
        <Link to="/resources" className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft className="mr-1 h-4 w-4" /> Back to Resources
        </Link>
        <h1 className="font-outfit text-3xl font-bold tracking-tight flex items-center gap-2">
          <Calculator className="h-7 w-7 text-emerald-600" />
          Missed Opportunity ROI Estimator
        </h1>
        <p className="text-muted-foreground mt-1 max-w-2xl">
          Run a live ROI projection with your prospect. Email them a co-branded PDF, or drop their numbers into a
          ProspectMax deck so the conversation picks up exactly where you left off.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-5">
        {/* Inputs */}
        <Card className="lg:col-span-3 border">
          <CardContent className="pt-6 space-y-4">
            <div>
              <h3 className="font-semibold text-sm uppercase text-emerald-700 tracking-wide">Their numbers</h3>
              <p className="text-xs text-muted-foreground mt-0.5">All five are required for an accurate projection.</p>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Missed new business calls <span className="text-muted-foreground">/ day</span></Label>
                <Input type="number" min="0" step="0.5" placeholder="e.g. 1" value={inputs.missed_calls_daily}
                       onChange={(e) => setInputs({ ...inputs, missed_calls_daily: e.target.value })}
                       data-testid="input-missed-calls" />
                <p className="text-[11px] text-muted-foreground">× 30 = {fmtInt(numericInputs.missed_calls_daily * 30)} / month</p>
              </div>
              <div className="space-y-1.5">
                <Label>Missed website leads <span className="text-muted-foreground">/ day</span></Label>
                <Input type="number" min="0" step="0.5" placeholder="e.g. 0.5" value={inputs.missed_website_leads_daily}
                       onChange={(e) => setInputs({ ...inputs, missed_website_leads_daily: e.target.value })}
                       data-testid="input-missed-leads" />
                <p className="text-[11px] text-muted-foreground">× 30 = {fmtInt(numericInputs.missed_website_leads_daily * 30)} / month</p>
              </div>
              <div className="space-y-1.5">
                <Label>Close / conversion rate <span className="text-muted-foreground">(%)</span></Label>
                <Input type="number" min="0" max="100" step="1" placeholder="e.g. 15" value={inputs.conversion_rate_pct}
                       onChange={(e) => setInputs({ ...inputs, conversion_rate_pct: e.target.value })}
                       data-testid="input-conversion-rate" />
              </div>
              <div className="space-y-1.5">
                <Label>Customer lifetime value <span className="text-muted-foreground">($)</span></Label>
                <Input type="number" min="0" step="50" placeholder="e.g. 1500" value={inputs.lifetime_value}
                       onChange={(e) => setInputs({ ...inputs, lifetime_value: e.target.value })}
                       data-testid="input-ltv" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Monthly program cost <span className="text-muted-foreground">($)</span></Label>
                <Input type="number" min="0" step="25" placeholder="e.g. 695" value={inputs.monthly_program_cost}
                       onChange={(e) => setInputs({ ...inputs, monthly_program_cost: e.target.value })}
                       data-testid="input-monthly-cost" />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-2 text-xs">
              <span className="text-muted-foreground self-center">Quick presets:</span>
              <button type="button" className="px-2 py-1 rounded-full bg-zinc-100 hover:bg-zinc-200"
                      onClick={() => presetClick({ missed_calls_daily: "1", missed_website_leads_daily: "0.5",
                                                  conversion_rate_pct: "15", lifetime_value: "1500", monthly_program_cost: "695" })}
                      data-testid="preset-google-sheet">Google Sheet example</button>
              <button type="button" className="px-2 py-1 rounded-full bg-zinc-100 hover:bg-zinc-200"
                      onClick={() => presetClick({ missed_calls_daily: "2", missed_website_leads_daily: "1",
                                                  conversion_rate_pct: "20", lifetime_value: "2500", monthly_program_cost: "1295" })}
                      data-testid="preset-mid">Mid-range</button>
              <button type="button" className="px-2 py-1 rounded-full bg-zinc-100 hover:bg-zinc-200"
                      onClick={() => setInputs({ missed_calls_daily: "", missed_website_leads_daily: "",
                                                conversion_rate_pct: "", lifetime_value: "", monthly_program_cost: "" })}
                      data-testid="preset-clear">Clear</button>
            </div>

            <div className="border-t pt-4 mt-2">
              <h3 className="font-semibold text-sm uppercase text-emerald-700 tracking-wide mb-2">Send to prospect (optional)</h3>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label className="text-xs">Name</Label>
                  <Input value={prospect.name} onChange={(e) => setProspect({ ...prospect, name: e.target.value })} data-testid="prospect-name" /></div>
                <div><Label className="text-xs">Company</Label>
                  <Input value={prospect.company} onChange={(e) => setProspect({ ...prospect, company: e.target.value })} data-testid="prospect-company" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Email</Label>
                  <Input type="email" value={prospect.email} onChange={(e) => setProspect({ ...prospect, email: e.target.value })} data-testid="prospect-email" placeholder="them@example.com" /></div>
                <div className="sm:col-span-2"><Label className="text-xs">Personal note (optional)</Label>
                  <Textarea rows={2} value={prospect.note} onChange={(e) => setProspect({ ...prospect, note: e.target.value })} data-testid="prospect-note" placeholder="Hi — great talking with you today. Here's the projection we discussed…" /></div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Live projection + actions */}
        <Card className="lg:col-span-2 border bg-gradient-to-br from-emerald-50/40 via-white to-teal-50/40">
          <CardContent className="pt-6 space-y-4">
            <h3 className="font-semibold text-sm uppercase text-emerald-800 tracking-wide">Live projection</h3>

            <div className="space-y-1.5 text-sm" data-testid="roi-projection">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total missed opportunities / mo</span>
                <span className="font-semibold tabular-nums">{fmtInt(outputs.total_missed_opportunities_monthly)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">New customers / mo</span>
                <span className="font-semibold tabular-nums">{outputs.new_customers_monthly.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">New customer revenue / mo</span>
                <span className="font-semibold tabular-nums">{fmtMoney(outputs.new_customer_revenue)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Less monthly cost</span>
                <span className="tabular-nums">− {fmtMoney(numericInputs.monthly_program_cost)}</span>
              </div>
            </div>

            <div className="rounded-lg bg-gradient-to-r from-emerald-700 to-emerald-600 text-white p-4 space-y-2">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-emerald-100">Monthly gross profit</div>
                <div className="text-3xl font-bold tabular-nums" data-testid="output-gross-profit">{fmtMoney(outputs.gross_profit)}</div>
              </div>
              <div className="pt-2 border-t border-emerald-400/40">
                <div className="text-[11px] uppercase tracking-wide text-emerald-100">Monthly ROI</div>
                <div className="text-3xl font-bold tabular-nums" data-testid="output-roi-pct">
                  {Number.isFinite(outputs.roi_pct) ? `${Math.round(outputs.roi_pct).toLocaleString()}%` : "—"}
                </div>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Button onClick={handleEmail} disabled={sending || !allInputsFilled || !prospect.email}
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      data-testid="email-roi-pdf">
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Email PDF to prospect
              </Button>
              <Button onClick={handleDownloadDeck} disabled={downloading || !allInputsFilled}
                      variant="outline" className="w-full border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                      data-testid="download-prospectmax-with-roi">
                {downloading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
                Download ProspectMax deck with these numbers
              </Button>
              {!allInputsFilled && (
                <p className="text-[11px] text-muted-foreground text-center">Fill in all five inputs to enable export.</p>
              )}
            </div>

            <p className="text-[11px] text-muted-foreground border-t pt-3 leading-relaxed">
              Estimates only. Real-world results depend on offer, response speed, and lead-working diligence.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
